import React, { useEffect, useState } from 'react';
import { Shield, Plus, ExternalLink, Link as LinkIcon, Tag, Users, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { organizationService } from '../../services/organizationService';
import { governanceOverlaysService } from '../../services/governanceOverlaysService';
import { GovernanceOverlay } from '../../types/governanceOverlays';
import ConfirmDeleteModal from '../ConfirmDeleteModal';

interface Props {
  organizationId: string;
  accessToken: string | null;
  setUserAndToken: (user: any, token: string) => void;
}

const GovernanceOverlaysTab: React.FC<Props> = ({ organizationId, accessToken, setUserAndToken }) => {
  const [connected, setConnected] = useState<GovernanceOverlay[]>([]);
  const [allOverlays, setAllOverlays] = useState<GovernanceOverlay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [overlayToDisconnect, setOverlayToDisconnect] = useState<GovernanceOverlay | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (organizationId && accessToken) {
      fetchConnected();
    }
  }, [organizationId, accessToken]);

  const fetchConnected = async () => {
    try {
      setLoading(true);
      const data = await organizationService.getOrganizationGovernanceOverlays(organizationId, accessToken, setUserAndToken);
      // We only have brief fields; treat as minimal GovernanceOverlay
      setConnected(data as any);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch governance overlays');
    } finally {
      setLoading(false);
    }
  };

  const openModal = async () => {
    setSelectedIds(connected.map(a => a.id));
    setShowModal(true);
    if (allOverlays.length === 0) {
      try {
        setModalLoading(true);
        const data = await governanceOverlaysService.getGovernanceOverlays(accessToken, setUserAndToken);
        setAllOverlays(data);
      } finally {
        setModalLoading(false);
      }
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const saveConnections = async () => {
    try {
      setSaving(true);
      await organizationService.connectGovernanceOverlaysToOrganization(organizationId, { governance_overlay_ids: selectedIds }, accessToken, setUserAndToken);
      await fetchConnected();
      setShowModal(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save connections');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnectClick = (overlay: GovernanceOverlay) => {
    setOverlayToDisconnect(overlay);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    if (!overlayToDisconnect) return;
    try {
      setDisconnecting(true);
      await organizationService.disconnectGovernanceOverlayFromOrganization(
        organizationId,
        overlayToDisconnect.id,
        accessToken,
        setUserAndToken
      );
      
      // Refresh connected overlays
      await fetchConnected();
      setShowDisconnectModal(false);
      setOverlayToDisconnect(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to disconnect overlay');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading governance overlays...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>
    );
  }

  return (
    <div>
      {/* Connected list */}
      <div className="p-6 pt-0">
        {/* Header - match CustomerCategoriesTab structure */}
        <div className="flex items-center justify-between py-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Governance Overlays</h3>
            <p className="text-sm text-gray-600">Manage the governance overlays of this organization</p>
          </div>
          <button
            onClick={openModal}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition"
          >
            <LinkIcon className="w-4 h-4" />
            Connect Governance Overlays
          </button>
        </div>

        {/* Connected Overlays */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {connected.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-8 px-4">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Governance Overlays Connected</h3>
            <p className="text-slate-500 text-center max-w-md">
              Connect governance overlays to define the governance protocols and rules for this organization.
            </p>
          </div>
        ) : (
          connected.map(a => {
            const order = undefined; // not shown on card if unavailable
            const propertiesCount = (a as any).descriptions ? (a as any).descriptions.length : undefined;
            return (
              <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Shield className="w-6 h-6 text-slate-700" />
                    <div>
                      <Link
                        to={`/governance-overlays/${a.id}`}
                        className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      >
                        {a.name}
                      </Link>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <a
                      href={`/governance-overlays/${a.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="View details in new tab"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => handleDisconnectClick(a)}
                      className="text-red-400 hover:text-red-600 transition-colors"
                      title="Disconnect overlay"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{a.description}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-gray-500">{order !== undefined ? `Order: ${order}` : ''}</span>
                  {propertiesCount !== undefined && (
                    <span className="text-xs text-gray-500">{propertiesCount} properties</span>
                  )}
                </div>
              </div>
            );
          })
        )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-xl overflow-hidden">
            <div className="px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Connect Governance Overlays</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-slate-700">Close</button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {modalLoading ? (
                <div className="flex items-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div><span className="ml-2 text-slate-600">Loading...</span></div>
              ) : (
                allOverlays.map(a => (
                  <label key={a.id} className="flex items-start gap-3 p-3 border-b last:border-b-0">
                    <input type="checkbox" checked={selectedIds.includes(a.id)} onChange={() => toggleSelect(a.id)} className="mt-1" />
                    <div>
                      <div className="font-medium text-slate-900">{a.name}</div>
                      <div className="text-sm text-slate-600">{a.description}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800">Cancel</button>
              <button onClick={saveConnections} disabled={saving} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition flex items-center gap-2">{saving ? 'Saving...' : (<><LinkIcon className="w-4 h-4"/>Save Connections</>)}</button>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setOverlayToDisconnect(null);
        }}
        onConfirm={confirmDisconnect}
        title="Disconnect Governance Overlay"
        message="Are you sure you want to disconnect this governance overlay from the organization? This will remove the overlay from all governance protocols and rules for this organization."
        itemName={overlayToDisconnect?.name || ''}
        isLoading={disconnecting}
      />
    </div>
  );
};

export default GovernanceOverlaysTab;
